import {
    AfterViewChecked,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewChildren,
    QueryList,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatMenuModule } from '@angular/material/menu';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { MatDialogSizes } from '../../../enums/mat-dialog-sizes.enum';
import { JwtLegFiClaims } from '../../../services/auth/jwt-legfi-claims.model';
import { LegFiJwtService } from '../../../services/auth/legfi-jwt.service';
import {
    GableChatMessage,
    GableDrawerService,
    GableMode,
} from '../../../services/ai-poc/gable-drawer.service';
import {
    ActionPreviewCardPayload,
    ActionPreviewEdits,
    AssistantPocCitation,
    AssistantPocConversationSummary,
    AssistantPocFeedback,
    AssistantPocFeedbackChip,
    AssistantPocPendingProposal,
    AssistantPocQuestionAnswer,
    AssistantPocToolCard,
    AiPocService,
    BudgetRedirectCardPayload,
    DeliverableCardPayload,
    GenerateReportCardPayload,
    isActionPreviewCardPayload,
    isBudgetRedirectCardPayload,
    isDeliverableCardPayload,
    isGenerateReportCardPayload,
} from '../../../services/ai-poc/ai-poc.service';
import { AssistantPocPendingQuestions } from '../../../services/ai-poc/ai-poc-stream.service';
import { HighlightPipe } from '../../../pipes/highlight.pipe';
import { MarkdownPipe } from '../../../pipes/markdown.pipe';
import {
    ApprovalDialogComponent,
    ApprovalDialogData,
    ApprovalDialogResult,
} from '../../v2-layout/ai-poc/approval-dialog/approval-dialog.component';
import {
    TeachGableDialogComponent,
    TeachGableDialogData,
    TeachGableDialogResult,
} from '../../v2-layout/ai-poc/teach-gable-dialog/teach-gable-dialog.component';
import { GrowlerService } from '../../shared/growler/growler.service';
import { QuestionCardComponent } from '../../v2-layout/ai-poc/question-card/question-card.component';
import { GableBudgetRedirectCardComponent } from '../../v2-layout/ai-poc/budget-redirect-card/budget-redirect-card.component';
import { GableActionPreviewCardComponent } from '../../v2-layout/ai-poc/action-preview/action-preview-card.component';
import { GableReportCardComponent } from '../../v2-layout/ai-poc/report-card/report-card.component';
import { GableDeliverableCardComponent } from '../../v2-layout/ai-poc/deliverable-card/deliverable-card.component';
import { AiGableMascotComponent } from '../../v2-layout/shared/ai-gable-mascot/ai-gable-mascot.component';
// AiGableMascotComponent typed as a ViewChild ref; keep this import
// even though it looks duplicate — Angular needs both the runtime
// import (for the standalone component slot) and the type for the
// ViewChild generic below.
import { AiThoughtTrailComponent } from '../../v2-layout/shared/ai-thought-trail/ai-thought-trail.component';
import { AlertBlockComponent } from '../../v2-layout/shared/alert-block/alert-block.component';
import { FlexSpacerDirective } from '../../v2-layout/shared/directives/flex-spacer.directive';
import { GableDrawerResizeDirective } from '../directives/gable-drawer-resize.directive';
import { GableMessageHighlightDirective } from './gable-message-highlight.directive';
import { GableFeedbackPanelComponent } from '../gable-feedback-panel/gable-feedback-panel.component';

interface GableQuickAction {
    id: 'popular' | 'finance' | 'reports' | 'units' | 'violations' | 'requests' | 'comms';
    label: string;
    icon: string | null;
}

interface GableSuggestion {
    label: string;
    prompt: string;
}

/**
 * Omnipresent Gable AI drawer — slides in from the right edge over the
 * router-outlet on every authenticated route. Thin view over
 * `GableDrawerService` (which owns conversation, streaming, width, etc.).
 *
 * Layout:
 *   [ header — title, dock-toggle, close ]
 *   [ scrollable body — empty-state OR conversation OR pending approvals ]
 *   [ composer — textarea + send button ]
 */
@UntilDestroy()
@Component({
    selector: 'app-gable-drawer-host',
    templateUrl: './gable-drawer-host.component.html',
    styleUrls: ['./gable-drawer-host.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        MatButtonModule,
        MatDialogModule,
        MatDividerModule,
        MatMenuModule,
        MatFormFieldModule,
        MatInputModule,
        AiGableMascotComponent,
        AiThoughtTrailComponent,
        AlertBlockComponent,
        FlexSpacerDirective,
        GableActionPreviewCardComponent,
        GableBudgetRedirectCardComponent,
        GableDrawerResizeDirective,
        GableMessageHighlightDirective,
        GableFeedbackPanelComponent,
        GableReportCardComponent,
        GableDeliverableCardComponent,
        HighlightPipe,
        MarkdownPipe,
        QuestionCardComponent,
        TeachGableDialogComponent,
    ],
})
export class GableDrawerHostComponent implements OnInit, OnDestroy, AfterViewChecked {
    @ViewChild('messageInput') messageInputRef?: ElementRef<HTMLTextAreaElement>;
    @ViewChild('conversationScroller') conversationScrollerRef?: ElementRef<HTMLElement>;
    @ViewChild('emptyMascot') emptyMascotRef?: AiGableMascotComponent;
    @ViewChildren(GableActionPreviewCardComponent)
    actionPreviewCardsRef?: QueryList<GableActionPreviewCardComponent>;

    /** Quick-action chip strip — drives the suggestion list rendered below. */
    readonly quickActions: ReadonlyArray<GableQuickAction> = [
        { id: 'popular', label: 'Popular', icon: 'ph-trend-up' },
        { id: 'finance', label: 'Finance', icon: 'ph-currency-circle-dollar' },
        { id: 'reports', label: 'Reports', icon: 'ph-file-text' },
        { id: 'units', label: 'Units', icon: 'ph-house-line' },
        { id: 'violations', label: 'Violations', icon: 'ph-seal-warning' },
        { id: 'requests', label: 'Requests', icon: 'ph-seal-question' },
        { id: 'comms', label: 'Comms', icon: 'ph-megaphone' },
    ];

    /** Suggestion sets per quick-action chip. */
    private readonly _suggestionsByAction: Record<GableQuickAction['id'], GableSuggestion[]> = {
        popular: [
            { label: 'Show me all open violations', prompt: 'Show me all open violations.' },
            { label: 'Which units have the most violations?', prompt: 'Which units have the most violations?' },
            { label: 'What can you tell me about my organization\'s data?', prompt: 'What can you tell me about my organization\'s data?' },
        ],
        finance: [
            { label: 'Show me this month\'s deposits', prompt: 'Show me this month\'s deposits.' },
            { label: 'Which owners are most past due?', prompt: 'Which owners are most past due?' },
        ],
        reports: [
            { label: 'Generate a delinquency report', prompt: 'Generate a delinquency report for the current period.' },
            { label: 'Generate a violation summary report', prompt: 'Generate a violation summary report.' },
        ],
        units: [
            { label: 'List all units missing an owner', prompt: 'List all units that don\'t currently have an owner assigned.' },
            { label: 'Which units have the most violations?', prompt: 'Which units have the most violations?' },
            { label: 'Show me units with overdue balances', prompt: 'Show me units with overdue balances.' },
        ],
        violations: [
            { label: 'Show me all open violations', prompt: 'Show me all open violations.' },
            { label: 'Which violation categories are most common?', prompt: 'Which violation categories are most common right now?' },
            { label: 'List violations created in the last 30 days', prompt: 'List violations created in the last 30 days.' },
        ],
        requests: [
            { label: 'Show me all open requests', prompt: 'Show me all open architectural and maintenance requests.' },
            { label: 'How many requests have been resolved this month?', prompt: 'How many requests have been resolved this month?' },
        ],
        comms: [
            { label: 'Draft a community-wide reminder about pool hours', prompt: 'Draft a community-wide reminder about pool hours.' },
            { label: 'Send a courtesy warning to specific units', prompt: 'Help me send a courtesy warning to specific units.' },
        ],
    };

    activeQuickAction: GableQuickAction['id'] = 'popular';

    isOpen = false;
    widthPx = 400;
    mode: GableMode = 'drawer';
    messages: GableChatMessage[] = [];
    pendingProposals: AssistantPocPendingProposal[] = [];
    apiSuggestions: string[] = [];
    pendingQuestions: AssistantPocPendingQuestions | null = null;
    isSending = false;
    draftMessage = '';

    // ----- Chat history (SD-6742) -----
    activeConversations: AssistantPocConversationSummary[] = [];
    archivedConversations: AssistantPocConversationSummary[] = [];
    activeTitle: string | null = null;
    historyLoading = false;
    /** True while a paged active-list fetch (search reset / load-more) runs. */
    conversationsLoading = false;
    /** Server reports another page of recent chats is available. */
    hasMoreConversations = false;
    /** Whether the expanded-mode sidebar is showing archived vs recent chats. */
    showArchived = false;
    /** Conversation whose title is being edited inline in the sidebar, if any. */
    editingConversationId: number | null = null;
    /** Working value for the inline rename input. */
    editingTitle = '';
    /** Cap the drawer-mode history menu to the N most-recent chats. */
    private static readonly DRAWER_MENU_RECENT_LIMIT = 10;
    /**
     * Search term for the expanded sidebar recent list. Server-side now
     * (SD-6742) — the bound value is debounced into a server call rather
     * than filtering the in-memory list. Kept as a field so the input stays
     * controlled and the empty-state copy can branch on "is a search active".
     */
    historySearch = '';
    /** px-from-bottom threshold that triggers the next-page fetch. */
    private static readonly INFINITE_SCROLL_THRESHOLD_PX = 80;

    /**
     * Auto-scroll bookkeeping.
     *
     * - When a new user message appears, scroll it to the TOP of the visible
     *   drawer so the user sees "the last thing I asked about" and watches
     *   the response stream in below it.
     * - While an assistant bubble is streaming AND the user hasn't scrolled
     *   up to read history, keep the bottom glued so new tokens stay visible.
     * - If the user scrolls up, stop force-sticking — they're reading. As
     *   soon as they scroll back near the bottom, re-stick.
     */
    private _lastUserMessageIndex = -1;
    private _isPinnedToBottom = true;
    private _lastScrollHeight = 0;
    /** Px-from-bottom under which "pinned" reactivates after the user
     *  scrolls back near the tail. Kept tight so we don't yank from
     *  partway up the history. */
    private static readonly STICK_THRESHOLD_PX = 40;

    /** Tracks which message currently has its inline feedback panel open. */
    private _feedbackPanelOpenFor: number | null = null;

    /**
     * When the user clicks Teach Gable from a thumbs-down panel, we stash
     * the source message here so we can resolve the close result back
     * onto the right interactionId (drawer mode swaps inline, so the
     * MatDialog `afterClosed` chain isn't carrying the msg reference).
     */
    private _pendingTeachMsg: GableChatMessage | null = null;

    /** Whether the inline Teach Gable view is showing in the drawer body. */
    drawerView: 'chat' | 'teach' = 'chat';

    /** Template alias so the HTML can read teachData without leading underscore. */
    get teachData(): TeachGableDialogData | null {
        return this._drawer.teachData;
    }

    constructor(
        private _drawer: GableDrawerService,
        private _dialog: MatDialog,
        private _cdr: ChangeDetectorRef,
        private _aiPoc: AiPocService,
        private _growler: GrowlerService,
    ) {}

    ngOnInit(): void {
        this._drawer.isOpen$.pipe(untilDestroyed(this)).subscribe((open) => {
            this.isOpen = open;
            this._cdr.markForCheck();
        });
        this._drawer.widthPx$.pipe(untilDestroyed(this)).subscribe((px) => {
            this.widthPx = px;
            this._cdr.markForCheck();
        });
        this._drawer.mode$.pipe(untilDestroyed(this)).subscribe((m) => {
            this.mode = m;
            this._cdr.markForCheck();
        });
        this._drawer.drawerView$.pipe(untilDestroyed(this)).subscribe((v) => {
            this.drawerView = v;
            this._cdr.markForCheck();
        });
        this._drawer.messages$.pipe(untilDestroyed(this)).subscribe((m) => {
            this.messages = m;
            this._cdr.markForCheck();
        });
        this._drawer.pendingProposals$.pipe(untilDestroyed(this)).subscribe((p) => {
            this.pendingProposals = p;
            this._cdr.markForCheck();
        });
        this._drawer.pendingQuestions$.pipe(untilDestroyed(this)).subscribe((q) => {
            this.pendingQuestions = q;
            this._cdr.markForCheck();
        });
        this._drawer.isSending$.pipe(untilDestroyed(this)).subscribe((s) => {
            this.isSending = s;
            this._cdr.markForCheck();
        });
        this._drawer.draftMessage$.pipe(untilDestroyed(this)).subscribe((d) => {
            this.draftMessage = d;
            this._cdr.markForCheck();
        });
        this._drawer.feedback$.pipe(untilDestroyed(this)).subscribe(() => {
            this._cdr.markForCheck();
        });
        this._drawer.apiSuggestions$.pipe(untilDestroyed(this)).subscribe((s) => {
            this.apiSuggestions = s;
            this._cdr.markForCheck();
        });
        this._drawer.activeConversations$.pipe(untilDestroyed(this)).subscribe((c) => {
            this.activeConversations = c;
            this._cdr.markForCheck();
        });
        this._drawer.archivedConversations$.pipe(untilDestroyed(this)).subscribe((c) => {
            this.archivedConversations = c;
            this._cdr.markForCheck();
        });
        this._drawer.activeTitle$.pipe(untilDestroyed(this)).subscribe((t) => {
            this.activeTitle = t;
            this._cdr.markForCheck();
        });
        this._drawer.historyLoading$.pipe(untilDestroyed(this)).subscribe((l) => {
            this.historyLoading = l;
            this._cdr.markForCheck();
        });
        this._drawer.conversationsLoading$.pipe(untilDestroyed(this)).subscribe((l) => {
            this.conversationsLoading = l;
            this._cdr.markForCheck();
        });
        this._drawer.hasMoreConversations$.pipe(untilDestroyed(this)).subscribe((h) => {
            this.hasMoreConversations = h;
            this._cdr.markForCheck();
        });

        this._drawer.loadPendingProposals();
        this._drawer.refreshConversations();
    }

    ngOnDestroy(): void {
        // Service owns stream cleanup — host doesn't tear down chat state on
        // navigation. (Drawer host is mounted at app root so this only fires
        // on full logout / app teardown.)
    }

    ngAfterViewChecked(): void {
        const scroller = this.conversationScrollerRef?.nativeElement;
        if (!scroller) {
            return;
        }

        // 1. If the user just submitted a new question, pin their bubble to
        //    the top of the visible drawer so the streaming reply unfolds
        //    underneath. This wins over "stick to bottom" — the user wants
        //    to see what they asked.
        const lastUserIndex = this._findLastUserMessageIndex();
        if (lastUserIndex !== -1 && lastUserIndex !== this._lastUserMessageIndex) {
            this._lastUserMessageIndex = lastUserIndex;
            const bubble = scroller.querySelector<HTMLElement>(
                `[data-user-msg-index="${lastUserIndex}"]`,
            );
            if (bubble) {
                // Top-align with a small gap from the body's top padding.
                scroller.scrollTop = bubble.offsetTop - scroller.offsetTop - 12;
                this._isPinnedToBottom = false;
                this._lastScrollHeight = scroller.scrollHeight;
                return;
            }
        }

        // 2. Only re-pin to the bottom when CONTENT GREW. CD fires for many
        //    reasons (mouse moves, scroll events, sibling state changes) —
        //    blindly resetting scrollTop on every tick fights the user when
        //    they scroll up after streaming ends. Comparing scrollHeight
        //    tells us "is there new content the user might want to see?".
        const grew = scroller.scrollHeight > this._lastScrollHeight;
        if (grew && this._isPinnedToBottom) {
            scroller.scrollTop = scroller.scrollHeight;
        }
        this._lastScrollHeight = scroller.scrollHeight;
    }

    /**
     * Bound from the template. Tracks whether the user has scrolled away
     * from the bottom so the streaming auto-scroll only kicks in when
     * they're actually watching the tail of the conversation.
     */
    onScroll(): void {
        const scroller = this.conversationScrollerRef?.nativeElement;
        if (!scroller) {
            return;
        }
        const distanceFromBottom = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
        this._isPinnedToBottom = distanceFromBottom <= GableDrawerHostComponent.STICK_THRESHOLD_PX;
    }

    private _findLastUserMessageIndex(): number {
        for (let i = this.messages.length - 1; i >= 0; i--) {
            if (this.messages[i].role === 'user') {
                return i;
            }
        }
        return -1;
    }

    // -------------------------------------------------------------------------
    // Header chrome
    // -------------------------------------------------------------------------

    get firstName(): string {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (!jwt?.givenNames) {
            return 'there';
        }
        return jwt.givenNames.trim().split(/\s+/)[0] ?? 'there';
    }

    get orgName(): string {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        return jwt?.orgName ?? 'your organization';
    }

    get isEmptyState(): boolean {
        return this.messages.length === 0;
    }

    get hasPendingProposals(): boolean {
        return this.pendingProposals.length > 0;
    }

    get hasStreamingAssistant(): boolean {
        return this.messages.some((m) => m.role === 'assistant' && m.streamState === 'streaming');
    }

    /**
     * True while any sandbox deliverable in the conversation is still
     * generating (SD-7240). Used to hold back the thumbs/Teach footer +
     * feedback panel until the document card resolves — telemetry on a
     * turn that's still visibly working looks broken. The messages array
     * reference changes when the sandbox resolves (messages$ emission),
     * so OnPush re-evaluates this without manual nudging.
     */
    get hasPendingSandboxCard(): boolean {
        return this.messages.some((m) => m.sandboxState === 'generating');
    }

    get currentSuggestions(): GableSuggestion[] {
        return this._suggestionsByAction[this.activeQuickAction] ?? [];
    }

    get drawerStyle(): { [k: string]: string } {
        // `--gable-width` drives drawer mode (resizable). `--gable-expanded-width`
        // drives expanded mode — fixed to the Figma spec (1140px = 248 sidebar +
        // 892 chat) via the shared MatDialogSizes enum, capped to viewport in SCSS.
        return {
            '--gable-width': `${this.widthPx}px`,
            '--gable-expanded-width': MatDialogSizes.GABLE_EXPANDED,
        };
    }

    onClose(): void {
        this._drawer.close();
    }

    onNewChat(): void {
        this._drawer.startNewConversation();
    }

    // -------------------------------------------------------------------------
    // Chat history (SD-6742)
    // -------------------------------------------------------------------------

    /**
     * Header title for the chat view: the loaded conversation's title when one
     * is set, "New AI chat" on an empty fresh chat, otherwise "Gable AI".
     */
    get chatHeaderTitle(): string {
        if (this.activeTitle && this.activeTitle.trim() !== '') {
            return this.activeTitle.trim();
        }
        return this.isEmptyState ? 'Chat with Gable AI' : 'Gable AI';
    }

    /** Id of the conversation currently loaded — drives the sidebar highlight. */
    get activeConversationId(): number | null {
        return this._drawer.conversationId;
    }

    /** Drawer-mode history menu shows only the most-recent N chats (kept basic). */
    get recentMenuConversations(): AssistantPocConversationSummary[] {
        return this.activeConversations.slice(0, GableDrawerHostComponent.DRAWER_MENU_RECENT_LIMIT);
    }

    /** Display label for one history row — title, else first-user-message hint. */
    conversationLabel(c: AssistantPocConversationSummary): string {
        const title = (c.title ?? '').trim();
        return title !== '' ? title : 'New chat';
    }

    /** True when a search term is active — drives the empty-state copy branch. */
    get isSearchingHistory(): boolean {
        return this.historySearch.trim() !== '';
    }

    /** Pull fresh history when the drawer-mode title menu opens. */
    onHistoryMenuOpened(): void {
        this._drawer.refreshConversations();
    }

    /**
     * Sidebar search input handler. Server-side now (SD-6742): the term is
     * pushed into the service's debounced (~300ms) switchMap pipeline, which
     * resets to page 1 and matches title OR message content. No client-side
     * filtering.
     */
    onHistorySearchChange(term: string): void {
        this.historySearch = term;
        this._drawer.searchConversations(term);
        this._cdr.markForCheck();
    }

    /**
     * Infinite-scroll for the recent-chats rail. When the list scrolls within
     * a threshold of the bottom AND the server says more pages exist, fetch the
     * next page (appended in the service). The service guards against
     * concurrent / no-more fetches, so spamming this is safe.
     */
    onSidebarListScroll(event: Event): void {
        if (!this.hasMoreConversations || this.conversationsLoading) {
            return;
        }
        const el = event.target as HTMLElement | null;
        if (!el) {
            return;
        }
        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        if (distanceFromBottom <= GableDrawerHostComponent.INFINITE_SCROLL_THRESHOLD_PX) {
            this._drawer.loadMoreConversations();
        }
    }

    onSelectConversation(c: AssistantPocConversationSummary): void {
        if (this.isSending) {
            return;
        }
        this._drawer.loadConversation(c.id);
    }

    toggleShowArchived(): void {
        this.showArchived = !this.showArchived;
        this._cdr.markForCheck();
    }

    /**
     * Rename happens INLINE in the expanded sidebar (no modal — the expanded
     * drawer is itself a dialog, so a modal-on-modal reads badly). Clicking
     * Rename swaps the row's title for an input; commit on Enter/blur, cancel
     * on Escape. (Drawer mode has no rename affordance at all.)
     */
    onRenameConversation(c: AssistantPocConversationSummary): void {
        this.editingConversationId = c.id;
        this.editingTitle = this.conversationLabel(c);
        this._cdr.markForCheck();
        // Defer focus until the input has rendered in place of the title.
        setTimeout(() => {
            const input = document.querySelector<HTMLInputElement>('.gable-sidebar-rename-input');
            input?.focus();
            input?.select();
        });
    }

    /** Commit an inline rename. Guarded so a post-Escape blur is a no-op. */
    commitInlineRename(c: AssistantPocConversationSummary): void {
        if (this.editingConversationId !== c.id) {
            return;
        }
        const newTitle = this.editingTitle.trim();
        const current = this.conversationLabel(c);
        this.editingConversationId = null;
        this._cdr.markForCheck();

        if (newTitle === '' || newTitle === current) {
            return; // nothing to do
        }
        this._drawer
            .renameConversation(c.id, newTitle)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: () => this._growler.success('Chat renamed', 'The chat name was updated.'),
                error: (err) => this._growler.fromErrorResponse(err),
            });
    }

    cancelInlineRename(): void {
        this.editingConversationId = null;
        this._cdr.markForCheck();
    }

    /**
     * Archive with no confirmation — just do it and toast it. We do NOT
     * auto-open the Archived section or highlight the row: that read as a false
     * "selected" state. The user opens Archived themselves to find it.
     */
    onArchiveConversation(c: AssistantPocConversationSummary): void {
        this._drawer
            .archiveConversation(c.id)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: () => this._growler.success('Chat archived', 'Moved to archived chats.'),
                error: (err) => this._growler.fromErrorResponse(err),
            });
    }

    /**
     * Restore = the user is deliberately bringing this chat back, so select it:
     * load it into the chat view (which makes it the active, bolded row in the
     * recent list) on top of moving it out of Archived.
     */
    onUnarchiveConversation(c: AssistantPocConversationSummary): void {
        this._drawer
            .unarchiveConversation(c.id)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: () => {
                    this._growler.success('Chat restored', 'Moved back to recent chats.');
                    this._drawer.loadConversation(c.id);
                },
                error: (err) => this._growler.fromErrorResponse(err),
            });
    }

    trackByConversationId(_: number, c: AssistantPocConversationSummary): number {
        return c.id;
    }

    /**
     * Toggle between drawer (right-edge slide) and expanded (full-width
     * centered dialog) modes. Persisted via UserPreferencesService so the
     * choice sticks across sessions.
     */
    onToggleDock(): void {
        this._drawer.toggleMode();
    }

    get isExpanded(): boolean {
        return this.mode === 'expanded';
    }

    onSelectQuickAction(id: GableQuickAction['id']): void {
        this.activeQuickAction = id;
        this._cdr.markForCheck();
    }

    // -------------------------------------------------------------------------
    // Composer
    // -------------------------------------------------------------------------

    onDraftChange(text: string): void {
        this._drawer.setDraft(text);
    }

    onKeydown(event: KeyboardEvent): void {
        if (event.isComposing) {
            return;
        }
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            this.onSend();
        }
    }

    onStopStream(): void {
        this._drawer.cancelStream();
    }

    onSend(): void {
        const text = (this.draftMessage ?? '').trim();
        if (!text || this.isSending) {
            return;
        }
        this._drawer.sendMessage(text);
    }

    onSuggestionClick(suggestion: GableSuggestion): void {
        if (this.isSending) {
            return;
        }
        this._drawer.sendMessage(suggestion.prompt);
    }

    /**
     * Click on an AI-supplied follow-up suggestion — sends the suggestion
     * text as the next user message. Mirrors the empty-state suggestion
     * click but takes a plain string (`apiSuggestions` is `string[]`).
     */
    onApiSuggestionClick(text: string): void {
        if (this.isSending) {
            return;
        }
        const trimmed = (text ?? '').trim();
        if (trimmed === '') {
            return;
        }
        this._drawer.sendMessage(trimmed);
    }

    trackByApiSuggestion(_: number, text: string): string {
        return text;
    }

    /**
     * Render the follow-up suggestion strip only under the most recent
     * assistant bubble (the Figma's "↳ Schedule for tomorrow / ↳ Add SMS
     * / ↳ Escalate to First Warning" rows). Returns true only when the
     * given message index is the last assistant message AND:
     *   - its stream is complete (don't show suggestions while typing)
     *   - it didn't emit an action-preview card. Suggestions on top of
     *     a still-pending action create the "I already approved" confusion
     *     (a suggestion that says "Switch to Final Notice" gets fired as
     *     a fresh user turn, which the model interprets as a new request
     *     instead of editing the current proposal). Action-preview cards
     *     own their own affordances — Edit, status dropdown, Add unit —
     *     so the follow-up strip is dead weight there.
     */
    isLastAssistantMessage(index: number): boolean {
        const msg = this.messages[index];
        if (!msg || msg.role !== 'assistant') {
            return false;
        }
        if (msg.streamState && msg.streamState !== 'complete') {
            return false;
        }
        if (this._hasActionPreviewCard(msg)) {
            return false;
        }
        for (let i = this.messages.length - 1; i > index; i -= 1) {
            if (this.messages[i].role === 'assistant') {
                return false;
            }
        }
        return true;
    }

    private _hasActionPreviewCard(msg: GableChatMessage): boolean {
        if (!msg.toolCards || msg.toolCards.length === 0) {
            return false;
        }
        return msg.toolCards.some((c) => c.tool === 'propose_action');
    }

    // -------------------------------------------------------------------------
    // Streaming bubble helpers (mirrors the POC page's template hooks)
    // -------------------------------------------------------------------------

    trackByIndex(index: number): number {
        return index;
    }

    trackByProposalId(_: number, p: AssistantPocPendingProposal): number {
        return p.id;
    }

    trackByQuickAction(_: number, q: GableQuickAction): string {
        return q.id;
    }

    trackBySuggestion(_: number, s: GableSuggestion): string {
        return s.label;
    }

    trackByCitation(_: number, c: AssistantPocCitation): string {
        return `${c.table}:${c.id}`;
    }

    trackByToolCard(index: number, _card: AssistantPocToolCard): number {
        return index;
    }

    asReportCardPayload(card: AssistantPocToolCard): GenerateReportCardPayload | null {
        if (card.tool !== 'generate_report') {
            return null;
        }
        if (!isGenerateReportCardPayload(card.payload)) {
            console.warn(
                `[gable-drawer-host] Skipping tool card with tool="${card.tool}": payload shape mismatch.`,
            );
            return null;
        }
        return card.payload;
    }

    asBudgetRedirectPayload(card: AssistantPocToolCard): BudgetRedirectCardPayload | null {
        if (card.tool !== 'redirect_to_budget') {
            return null;
        }
        if (!isBudgetRedirectCardPayload(card.payload)) {
            console.warn(
                `[gable-drawer-host] Skipping tool card with tool="${card.tool}": payload shape mismatch.`,
            );
            return null;
        }
        return card.payload;
    }

    asActionPreviewPayload(card: AssistantPocToolCard): ActionPreviewCardPayload | null {
        if (card.tool !== 'propose_action') {
            return null;
        }
        if (!isActionPreviewCardPayload(card.payload)) {
            console.warn(
                `[gable-drawer-host] Skipping tool card with tool="${card.tool}": payload shape mismatch.`,
            );
            return null;
        }
        return card.payload;
    }

    asDeliverableCardPayload(card: AssistantPocToolCard): DeliverableCardPayload | null {
        if (card.tool !== 'build_document') {
            return null;
        }
        if (!isDeliverableCardPayload(card.payload)) {
            console.warn(
                `[gable-drawer-host] Skipping tool card with tool="${card.tool}": payload shape mismatch.`,
            );
            return null;
        }
        return card.payload;
    }

    onActionPreviewConfirm(event: { proposalId: number; edits: ActionPreviewEdits }): void {
        const idempotencyKey = this._randomUuid();
        this._drawer
            .commitActionProposal(event.proposalId, idempotencyKey, event.edits)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (res) => {
                    // Patch the toolCard payload so the card re-renders
                    // into its "committed" final state via ngOnChanges,
                    // independent of any ViewChildren lookup timing. Carry
                    // the execute() result through so the collapsed view
                    // can show counts / deeplink to the destination.
                    this._drawer.patchActionPreviewState(
                        event.proposalId,
                        'committed',
                        res.result ?? null,
                    );
                    this._cdr.markForCheck();
                },
                error: (err) => {
                    console.error('[gable-drawer-host] commit action failed', err);
                    const card = this._findActionPreviewCard(event.proposalId);
                    if (card) {
                        card.submitting = false;
                    }
                    this._cdr.markForCheck();
                },
            });
    }

    onActionPreviewCancel(event: { proposalId: number }): void {
        this._drawer
            .rejectProposal(event.proposalId)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: () => {
                    this._drawer.patchActionPreviewState(event.proposalId, 'cancelled');
                    this._cdr.markForCheck();
                },
                error: (err) => {
                    console.error('[gable-drawer-host] reject failed', err);
                    // Even on a server error, flip the card visually so
                    // the user gets feedback. Backend may have already
                    // recorded the proposal as rejected; worst case it's
                    // a stale "pending" row but the user moved on.
                    this._drawer.patchActionPreviewState(event.proposalId, 'cancelled');
                    this._cdr.markForCheck();
                },
            });
    }

    onActionPreviewUtility(event: {
        proposalId: number;
        utilityActionId: string;
        edits: ActionPreviewEdits;
    }): void {
        // `add_unit` is handled inline in the recipients section now —
        // the picker expands within the card and emits an `(edit)` patch
        // with `recipients.added_ids` directly. Nothing reaches this
        // handler with that id anymore. Other utility ids
        // (restore_skipped, future utility buttons) flow through.
        this._postRefine(event.proposalId, event.utilityActionId, event.edits);
    }

    private _postRefine(proposalId: number, utilityActionId: string, edits: ActionPreviewEdits): void {
        this._drawer
            .refineActionProposal(proposalId, utilityActionId, edits)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (res) => {
                    // Patch the card payload in place inside the assistant
                    // message's toolCards. Backend has rewritten preview +
                    // edits — re-renders flow through the OnPush card.
                    this._drawer.patchActionPreview(res.proposalId, {
                        preview: res.preview,
                        edits: res.edits,
                    });
                    this._cdr.markForCheck();
                },
                error: (err) => {
                    console.error('[gable-drawer-host] refine failed', err);
                },
            });
    }

    private _findActionPreviewCard(proposalId: number): GableActionPreviewCardComponent | undefined {
        return this.actionPreviewCardsRef?.find((c) => {
            const p = c.payload as unknown as Record<string, unknown> | null;
            return (p?.['proposal_id'] ?? p?.['proposalId']) === proposalId;
        });
    }

    private _randomUuid(): string {
        // crypto.randomUUID is widely supported in evergreen browsers.
        // Fallback to a timestamp+random string in the rare unsupported case.
        const c = window.crypto;
        if (c && typeof c.randomUUID === 'function') {
            return c.randomUUID();
        }
        return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }

    elapsedSeconds(msg: GableChatMessage): number | null {
        if (!msg.streamStartedAt || !msg.streamEndedAt) {
            return null;
        }
        return Math.max(1, Math.round((msg.streamEndedAt - msg.streamStartedAt) / 1000));
    }

    onThoughtsCollapsedChange(messageId: number | undefined, collapsed: boolean): void {
        if (messageId === undefined) {
            return;
        }
        this._drawer.updateThoughtsCollapsed(messageId, collapsed);
    }

    // -------------------------------------------------------------------------
    // Question card
    // -------------------------------------------------------------------------

    onAnswers(answers: AssistantPocQuestionAnswer[]): void {
        const batch = this.pendingQuestions;
        if (!batch) {
            return;
        }
        const summary = this._summarizeAnswers(batch, answers);
        this._drawer.answerQuestions(answers, summary);
    }

    onCloseQuestionCard(): void {
        this._drawer.closeQuestionCard();
    }

    get submittingAnswers(): boolean {
        return this._drawer.submittingAnswers;
    }

    // -------------------------------------------------------------------------
    // Feedback
    // -------------------------------------------------------------------------

    getFeedback(messageId: number | undefined): AssistantPocFeedback | null {
        return this._drawer.getFeedback(messageId);
    }

    onFeedback(msg: GableChatMessage, feedback: 'good' | 'bad'): void {
        const messageId = msg.messageId;
        const interactionId = msg.interactionId;
        if (messageId === undefined || interactionId === undefined) {
            return;
        }
        const previous = this._drawer.getFeedback(messageId) ?? 'none';
        const next: AssistantPocFeedback = previous === feedback ? 'none' : feedback;
        this._drawer.setFeedback(messageId, next);
        this._drawer.recordFeedback(interactionId, next)
            .pipe(untilDestroyed(this))
            .subscribe({
                error: () => this._drawer.setFeedback(messageId, previous),
            });

        // Thumbs-down expands the inline feedback panel (reason chips +
        // Teach/Send paths). Thumbs-up or unsetting collapses it.
        if (feedback === 'bad' && next === 'bad') {
            this._feedbackPanelOpenFor = messageId;
        } else {
            this._feedbackPanelOpenFor = null;
        }
        this._cdr.markForCheck();
    }

    /** Whether the inline feedback panel is open for a given message. */
    isFeedbackPanelOpenFor(messageId: number | undefined): boolean {
        return messageId !== undefined && this._feedbackPanelOpenFor === messageId;
    }

    /** Cancel-all-the-panels helper (Skip path). */
    closeFeedbackPanel(): void {
        this._feedbackPanelOpenFor = null;
        this._cdr.markForCheck();
    }

    // --- Inline feedback panel event handlers ---

    onChipFeedback(msg: GableChatMessage, event: { chip: AssistantPocFeedbackChip; detail?: string }): void {
        const interactionId = msg.interactionId;
        if (interactionId === undefined) { return; }
        this._aiPoc.recordChipFeedback(interactionId, event.chip, event.detail)
            .pipe(untilDestroyed(this))
            .subscribe();
        // Lock further feedback on this interaction and swap the thumbs
        // row out for the green "submitted" confirmation.
        this._drawer.markFeedbackSent(interactionId);
        this.closeFeedbackPanel();
        this._cdr.markForCheck();
    }

    onTeachRequestedFromChip(
        msg: GableChatMessage,
        event: { chip: AssistantPocFeedbackChip },
    ): void {
        // NOTE: do NOT markFeedbackSent here. We don't want the green
        // confirmation banner appearing while the user is still in the
        // middle of writing the lesson — only after they close the
        // Teach view (save or cancel).
        this._openTeachGableDrawer(msg, event.chip, false);
        this.closeFeedbackPanel();
    }

    /**
     * Flag for team to review — telemetry-only. Records the chip on the
     * interaction event and marks feedback as sent. No lesson row, no
     * drawer. The team can find these via `metadata.feedback_chip` with
     * thumbs=bad in their dashboard.
     */
    onFlagRequestedFromChip(
        msg: GableChatMessage,
        event: { chip: AssistantPocFeedbackChip },
    ): void {
        const interactionId = msg.interactionId;
        if (interactionId === undefined) { return; }
        this._aiPoc
            .recordChipFeedback(interactionId, event.chip, '[flagged for team review]')
            .pipe(untilDestroyed(this))
            .subscribe();
        this._drawer.markFeedbackSent(interactionId);
        this.closeFeedbackPanel();
        this._cdr.markForCheck();
    }

    /** Renders the green "feedback submitted" confirmation instead of thumbs. */
    wasFeedbackSent(msg: GableChatMessage): boolean {
        return this._drawer.wasFeedbackSent(msg.interactionId);
    }

    /**
     * "Teach Gable from this answer" — the in-chat capture point that
     * sits next to the 👍/👎 icons on every assistant turn. Different
     * semantics from the feedback-chip path:
     *
     *   - **Trigger**: the conversation's FIRST user message (the
     *     original question users will ask again), NOT the message
     *     immediately preceding `msg`. If turn 1 was "what are total
     *     late fees?" and turn 2 was the user pushing back, we still
     *     want future "what are total late fees?" askers to hit this
     *     lesson — so the trigger lives on turn 1's prompt.
     *   - **Guidance seed**: the FULL content of `msg` (the corrected
     *     answer). Author edits/trims before save.
     *   - **Source event**: `msg.interactionId` so the backend can
     *     stamp `ai_interaction_events.promoted_lesson_id` and the
     *     AI Activity drawer renders "Already promoted" if the admin
     *     finds this turn there later.
     */
    onTeachFromThisAnswer(msg: GableChatMessage): void {
        const firstUserMsg = this.messages.find((m) => m.role === 'user');
        const triggerContext = (firstUserMsg?.content ?? '').trim();
        const guidance = (msg.content ?? '').trim();
        if (triggerContext === '' || guidance === '') {
            // Nothing useful to seed with — bail quietly. (Should be rare:
            // user has to be on a complete assistant message AND there
            // has to be a user message earlier in the conversation.)
            return;
        }
        const reasoningTrail = (msg.thoughts ?? [])
            .filter((t) => t.kind === 'summary')
            .map((t) => t.label);
        const data: TeachGableDialogData = {
            userQuestion: triggerContext,
            assistantAnswer: guidance,
            reasoningTrail,
            prefillDraft: {
                title: this._makeDraftTitle(triggerContext),
                triggerContext,
                guidance,
            },
            ...(msg.interactionId !== undefined ? { fromInteractionEventId: msg.interactionId } : {}),
        };

        if (this._drawer.mode === 'drawer') {
            this._pendingTeachMsg = msg;
            this._drawer.openTeachInDrawer(data);
            this._cdr.markForCheck();
            return;
        }

        this._dialog
            .open<TeachGableDialogComponent, TeachGableDialogData, TeachGableDialogResult>(
                TeachGableDialogComponent,
                {
                    panelClass: ['gable-teach-drawer'],
                    width: MatDialogSizes.MD,
                    data,
                },
            )
            .afterClosed()
            .pipe(untilDestroyed(this))
            .subscribe((result) => {
                if (result?.lesson && msg.interactionId !== undefined) {
                    this._drawer.markTaught(msg.interactionId);
                }
            });
    }

    /**
     * Mirror of the backend `AiActivityController::makeDraftTitle` —
     * keeps the in-chat capture's title seed consistent with the
     * Activity-page promote flow. First ~60 chars of the prompt,
     * ending at the last word boundary.
     */
    private _makeDraftTitle(prompt: string): string {
        const trimmed = prompt.trim();
        if (trimmed.length <= 60) { return trimmed; }
        const slice = trimmed.slice(0, 60);
        const lastSpace = slice.lastIndexOf(' ');
        return (lastSpace > 30 ? slice.slice(0, lastSpace) : slice).trim();
    }

    /**
     * Open the Teach Gable drawer for one assistant message. Used by the
     * inline feedback panel (Teach / Flag CTAs); also still works as an
     * explicit "Teach about this" button if needed elsewhere.
     */
    private _openTeachGableDrawer(
        msg: GableChatMessage,
        triggeringChip?: AssistantPocFeedbackChip,
        _startInFlagMode: boolean = false,
    ): void {
        const userQuestion = this.findPrecedingUserMessage(msg);
        const reasoningTrail = (msg.thoughts ?? [])
            .filter((t) => t.kind === 'summary')
            .map((t) => t.label);
        const data: TeachGableDialogData = {
            userQuestion,
            assistantAnswer: msg.content ?? '',
            reasoningTrail,
            triggeringChip,
        };

        // In drawer mode swap the drawer's body to the Teach view (no
        // stacked overlays). In expanded / dialog mode open Teach as its
        // own MatDialog — the underlying page still shows so there's no
        // awkward drawer-on-drawer.
        if (this._drawer.mode === 'drawer') {
            this._pendingTeachMsg = msg;
            this._drawer.openTeachInDrawer(data);
            this._cdr.markForCheck();
            return;
        }

        this._dialog
            .open<TeachGableDialogComponent, TeachGableDialogData, TeachGableDialogResult>(
                TeachGableDialogComponent,
                {
                    panelClass: ['gable-teach-drawer'],
                    data,
                    autoFocus: false,
                    restoreFocus: false,
                    hasBackdrop: false,
                    disableClose: true,
                },
            )
            .afterClosed()
            .pipe(untilDestroyed(this))
            .subscribe((result) => this._handleTeachClosed(result ?? null, msg));
    }

    /**
     * Bound on the inline `<app-teach-gable-dialog>` instance — fires
     * whatever the embedded form emits (save / cancel / flag).
     */
    onTeachClosed(result: TeachGableDialogResult): void {
        const msg = this._pendingTeachMsg;
        this._drawer.closeTeachInDrawer();
        this._pendingTeachMsg = null;
        this._handleTeachClosed(result, msg);
    }

    private _handleTeachClosed(result: TeachGableDialogResult, msg: GableChatMessage | null): void {
        if (msg?.interactionId !== undefined) {
            if (result) {
                // Saved or flagged — mark taught (for the 🎓 badge) AND
                // mark feedback-sent (for the green confirmation alert).
                this._drawer.markTaught(msg.interactionId);
                this._drawer.markFeedbackSent(msg.interactionId);

                // The MariaDB row is canonical; if the pgvector embed
                // failed the lesson is "saved" but won't fire in chat
                // retrieval until manually reindexed. Surface that at
                // save-time instead of waiting for the admin to notice
                // via the management page "needs reindex" cue.
                if (result.lesson?.embedStatus === 'pending') {
                    this._growler.error(
                        'Gable lessons',
                        'Lesson saved, but the vector index could not be built. A manual reindex is needed before Gable will use it.',
                    );
                }
            }
            // Cancelled (result is null): do nothing — leave the user
            // free to re-thumbs-down and try again.
        }
        this._cdr.markForCheck();
    }

    wasTaught(msg: GableChatMessage): boolean {
        return this._drawer.wasTaught(msg.interactionId);
    }

    trackByAppliedLessonId(_idx: number, l: { id: number }): number {
        return l.id;
    }

    /**
     * Walk backwards from the assistant turn that got the thumbs-down to
     * find the last user message — that's the question Gable was answering.
     */
    private findPrecedingUserMessage(target: GableChatMessage): string {
        const all = this._drawer.messages;
        const idx = all.indexOf(target);
        if (idx < 0) { return ''; }
        for (let i = idx - 1; i >= 0; i--) {
            const m = all[i];
            if (m.role === 'user' && m.content.trim() !== '') {
                return m.content;
            }
        }
        return '';
    }

    get isSuperUser(): boolean {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        return !!jwt?.superUser;
    }

    // -------------------------------------------------------------------------
    // Proposals
    // -------------------------------------------------------------------------

    get proposalInFlight(): number | null {
        return this._drawer.proposalInFlight;
    }

    onReviewProposal(proposal: AssistantPocPendingProposal): void {
        if (this._drawer.proposalInFlight === proposal.id) {
            return;
        }
        const data: ApprovalDialogData = { proposal };
        this._dialog
            .open<ApprovalDialogComponent, ApprovalDialogData, ApprovalDialogResult>(
                ApprovalDialogComponent,
                {
                    panelClass: ['v2-dialog'],
                    width: MatDialogSizes.SM,
                    data,
                    autoFocus: false,
                },
            )
            .afterClosed()
            .pipe(untilDestroyed(this))
            .subscribe((result) => {
                if (result === 'approve') {
                    this._commitProposal(proposal);
                } else if (result === 'reject') {
                    this._rejectProposal(proposal);
                }
            });
    }

    onCancelProposal(proposal: AssistantPocPendingProposal): void {
        if (this._drawer.proposalInFlight === proposal.id) {
            return;
        }
        this._rejectProposal(proposal);
    }

    private _commitProposal(proposal: AssistantPocPendingProposal): void {
        const idempotencyKey = crypto.randomUUID();
        this._drawer.proposalInFlight = proposal.id;
        this._cdr.markForCheck();
        this._drawer.approveProposal(proposal.id, idempotencyKey)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (res) => {
                    this._drawer.proposalInFlight = null;
                    this._drawer.removeProposal(proposal.id);
                    const friendly = this._friendlyActionDescription(proposal);
                    const note = res.idempotent
                        ? `${friendly} — already done previously, no duplicate created.`
                        : `${friendly}.`;
                    this._drawer.appendMessage({
                        role: 'assistant',
                        content: note,
                        timestamp: new Date(),
                        alert: 'success',
                    });
                },
                error: () => {
                    this._drawer.proposalInFlight = null;
                    this._drawer.appendMessage({
                        role: 'assistant',
                        content: 'Sorry — committing that proposal failed. It may have expired or been rejected by another tab. Try again, or ask the assistant to repropose.',
                        timestamp: new Date(),
                    });
                },
            });
    }

    private _rejectProposal(proposal: AssistantPocPendingProposal): void {
        this._drawer.proposalInFlight = proposal.id;
        this._cdr.markForCheck();
        this._drawer.rejectProposal(proposal.id)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: () => {
                    this._drawer.proposalInFlight = null;
                    this._drawer.removeProposal(proposal.id);
                    this._drawer.appendMessage({
                        role: 'assistant',
                        content: `Cancelled the ${proposal.operation} on ${proposal.modelClass}. Nothing was changed.`,
                        timestamp: new Date(),
                        alert: 'info',
                    });
                },
                error: () => {
                    this._drawer.proposalInFlight = null;
                    this._cdr.markForCheck();
                },
            });
    }

    private _friendlyActionDescription(proposal: AssistantPocPendingProposal): string {
        const verb = {
            create: 'created',
            update: 'updated',
            delete: 'deleted',
        }[proposal.operation] ?? proposal.operation;
        const payload = proposal.payload ?? {};
        const primary = (payload['name'] ?? payload['title'] ?? null) as string | null;
        const friendlyModel = proposal.modelClass === 'ViolationStatement' ? 'Violation' : proposal.modelClass;
        if (primary) {
            return `${friendlyModel} "${primary}" ${verb}`;
        }
        if (proposal.entityId !== null) {
            return `${friendlyModel} #${proposal.entityId} ${verb}`;
        }
        return `${friendlyModel} ${verb}`;
    }

    private _summarizeAnswers(
        batch: AssistantPocPendingQuestions,
        answers: AssistantPocQuestionAnswer[],
    ): string {
        const byId = new Map(batch.questions.map((q) => [q.id, q]));
        let parts: string[] = [];
        for (const a of answers) {
            const q = byId.get(a.id);
            if (!q) {
                continue;
            }
            const label = q.prompt.replace(/[?:]+\s*$/, '');
            const optById = new Map((q.options ?? []).map((o) => [o.value, o.label]));
            let valueText: string;
            if (Array.isArray(a.value)) {
                valueText = a.value
                    .map((v) => optById.get(String(v)) ?? String(v))
                    .filter((v) => v !== '')
                    .join(', ');
            } else if (typeof a.value === 'number') {
                valueText = String(a.value);
            } else {
                valueText = optById.get(a.value) ?? a.value;
            }
            if (valueText !== '') {
                parts = [...parts, `${label}: ${valueText}`];
            }
        }
        return parts.join(' · ');
    }
}
